import { useMemo } from "react";
import { Anchor, Box, ResponsiveContext } from "grommet";
import styled from "styled-components";

import { BoundaryLegendCategory, GeographyLevel, Screen } from "../models";
import BoundaryLegendIcon from "./BoundaryLegendIcon";
import GlossaryLink from "./GlossaryLink";
import InfoCircleIcon from "../icons/InfoCircleIcon";

interface Props {
  readonly screen: Screen;
  readonly geographyLevel: GeographyLevel;
  readonly showSmallText?: boolean;
  readonly showLinks?: boolean;
  readonly children?: React.ReactNode;
}

interface BoundaryLegendItemProps {
  readonly showSmallText: boolean;
}

const legendItemBaseStyle = `
  display: inline-flex;
  align-items: center;
  gap: .8rem;
  font-size: 1.4rem;
`;

const BoundaryLegendItem = styled.li<BoundaryLegendItemProps>`
  ${legendItemBaseStyle}
  color: var(--text-800);
  font-size: ${props => (props.showSmallText ? "1.1rem" : "1.4rem")};
`;

const BoundaryLegendLink = styled(Anchor)`
  ${legendItemBaseStyle}
  margin-left: auto;
  flex-shrink: 0;
  height: max-content;
  color: var(--highlight-800);
  &:hover {
    text-decoration: underline;
  }
`;

// The label for a legend entry is in the enum value, but for RiskCalcArea we want a glossary word
const boundaryLegendLabel = (item: BoundaryLegendCategory, showLinks: boolean) => {
  if (!showLinks) return item;
  if (item !== BoundaryLegendCategory.RiskCalcArea) return item;
  return <GlossaryLink term="risk_calculation_area">{item}</GlossaryLink>;
};

const BoundaryLegend = ({
  screen,
  geographyLevel,
  showSmallText = false,
  showLinks = true,
  children = <></>
}: Props) => {
  const legends = useMemo(() => {
    // Some of the entries are conditional. Making lists that might or might not have members and
    // including them with ... seems cleaner than putting all the conditionality in the main list.
    const riskCalcEntry =
      (screen === Screen.RiskToHomes || screen === Screen.WildfireLikelihood) &&
      geographyLevel !== GeographyLevel.state
        ? [BoundaryLegendCategory.RiskCalcArea]
        : [];
    const tractEntries =
      screen === Screen.VulnerablePopulations
        ? [BoundaryLegendCategory.SelectedTract, BoundaryLegendCategory.Tract]
        : [];
    const items = [
      BoundaryLegendCategory.Place,
      ...riskCalcEntry,
      BoundaryLegendCategory.County,
      BoundaryLegendCategory.State,
      ...tractEntries
    ];
    return items.map(item => (
      <BoundaryLegendItem key={item} showSmallText={showSmallText}>
        <BoundaryLegendIcon category={item} />
        {boundaryLegendLabel(item, showLinks)}
      </BoundaryLegendItem>
    ));
  }, [screen, geographyLevel, showSmallText, showLinks]);

  return (
    <ResponsiveContext.Consumer>
      {size => (
        <Box direction="row" gap="medium" pad={showSmallText ? "none" : "1rem"}>
          <Box
            as="ul"
            direction="row"
            wrap={true}
            role="list"
            style={{
              margin: 0,
              padding: 0,
              gap: "1rem 2rem"
            }}
            a11yTitle="Map legend"
          >
            {legends}
          </Box>
          {/* This section should not show in the map download component */}
          {size !== "small" && showLinks && (
            <>
              <BoundaryLegendLink href="#about-our-data" a11yTitle="Jump to About our data">
                <InfoCircleIcon color="var(--highlight-700)" size="16px" />
                Our data
              </BoundaryLegendLink>
              {children}
            </>
          )}
        </Box>
      )}
    </ResponsiveContext.Consumer>
  );
};

export default BoundaryLegend;
